import { useState, useCallback } from 'react';
import {
  inviteMember, addFamilyMember, removeFamilyMember,
  cancelMember, reactivateMember, syncBooking,
} from '../../services/primeService';
import { ApiError } from '../../services/api';
import type { PrimeActor, PrimeFamilyMember, PrimeMember } from '../../types/prime.types';

export interface PrimeActionResult {
  success: boolean;
  member?: PrimeMember;
  family?: PrimeFamilyMember[];
  mode?: string;
  status?: string;
  error?: string;
  code?: string;
  retryAfter?: number;
}

export interface UsePrimeActions {
  busy: boolean;
  invite: (phone: string) => Promise<PrimeActionResult>;
  addFamily: (handle: string, phone: string) => Promise<PrimeActionResult>;
  removeFamily: (handle: string, familyId: string) => Promise<PrimeActionResult>;
  cancel: (handle: string, opts?: { immediate?: boolean; reason?: string }) => Promise<PrimeActionResult>;
  reactivate: (handle: string, plan?: 'monthly' | 'yearly') => Promise<PrimeActionResult>;
  sync: (handle: string) => Promise<PrimeActionResult>;
}

function toFailure(err: unknown): PrimeActionResult {
  if (err instanceof ApiError) {
    if (err.statusCode === 429) {
      const secs = err.retryAfter ?? 30;
      return { success: false, error: `For mange forsøg. Prøv igen om ${secs} sek.`, code: err.code, retryAfter: secs };
    }
    return { success: false, error: err.message, code: err.code };
  }
  return { success: false, error: err instanceof Error ? err.message : 'Der opstod en fejl' };
}

export function usePrimeActions(actor: PrimeActor | null): UsePrimeActions {
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (fn: () => Promise<PrimeActionResult>): Promise<PrimeActionResult> => {
      if (!actor) return { success: false, error: 'Mangler medarbejder-kontekst.' };
      setBusy(true);
      try { return await fn(); }
      catch (err) { return toFailure(err); }
      finally { setBusy(false); }
    },
    // actor?.employeeId dækker aktør-skift.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actor?.employeeId]
  );

  const invite = useCallback((phone: string) =>
    run(async () => { await inviteMember(phone); return { success: true }; }), [run]);

  const addFamily = useCallback((handle: string, phone: string) =>
    run(async () => ({ success: true, family: await addFamilyMember(handle, phone) })), [run]);

  const removeFamily = useCallback((handle: string, familyId: string) =>
    run(async () => ({ success: true, family: await removeFamilyMember(handle, familyId) })), [run]);

  const cancel = useCallback((handle: string, opts?: { immediate?: boolean; reason?: string }) =>
    run(async () => ({ success: true, member: await cancelMember(handle, opts) })), [run]);

  const reactivate = useCallback((handle: string, plan?: 'monthly' | 'yearly') =>
    run(async () => { const r = await reactivateMember(handle, plan); return { success: true, member: r.member, mode: r.mode }; }), [run]);

  const sync = useCallback((handle: string) =>
    run(async () => { const r = await syncBooking(handle); return { success: true, status: r.status }; }), [run]);

  return { busy, invite, addFamily, removeFamily, cancel, reactivate, sync };
}
